import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { 
  CheckCircle, 
  XCircle,
  Clock,
  Eye,
  Calendar,
  FileText,
  User,
  School,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import SearchInput from '../components/SearchInput'
import { bookingService, Booking } from '../services/bookingService'
import { formatBookingSelectedPeriod } from '../utils/courseSchedule'

const Applications = () => {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [substatusFilter, setSubstatusFilter] = useState('ALL')
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [limit] = useState(10)

  useEffect(() => {
    setCurrentPage(1) // Reset to first page when filters change
  }, [searchTerm, statusFilter, substatusFilter])

  useEffect(() => {
    fetchBookings()
  }, [currentPage, searchTerm, statusFilter, substatusFilter])

  const fetchBookings = async () => {
    try {
      setLoading(true)
      const response = await bookingService.getBookings({
        page: currentPage,
        limit: limit,
        search: searchTerm || undefined,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        substatus: substatusFilter !== 'ALL' ? substatusFilter : undefined
      })
      
      if (response.success) {
        setBookings(response.data.bookings)
        setTotalPages(response.data.totalPages)
        setTotalCount(response.data.totalCount)
      }
    } catch (error) {
      console.error('Error fetching bookings:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string | undefined) => {
    if (!status) {
      return (
        <span className="badge badge-gray flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>Unknown</span>
        </span>
      )
    }
    const statusConfig: Record<string, { color: string; text: string; icon: any }> = {
      pending: { color: 'badge-warning', text: 'Pending', icon: Clock },
      approved: { color: 'badge-success', text: 'Approved', icon: CheckCircle },
      rejected: { color: 'badge-danger', text: 'Rejected', icon: XCircle },
      cancelled: { color: 'badge-gray', text: 'Cancelled', icon: XCircle }
    }
    const normalizedStatus = status.toLowerCase()
    const config = statusConfig[normalizedStatus] || statusConfig.pending
    const Icon = config.icon
    return (
      <span className={`badge ${config.color} flex items-center space-x-1`}>
        <Icon className="w-3 h-3" />
        <span>{config.text}</span>
      </span>
    )
  }

  const getSubstatusBadge = (substatus?: string) => {
    if (!substatus) return null
    const statusConfig: Record<string, { color: string; text: string; icon: any }> = {
      'pending approval from school': { color: 'badge-warning', text: 'Pending Approval', icon: Clock },
      'pending payment': { color: 'badge-warning', text: 'Pending Payment', icon: Clock }
    }
    const config = statusConfig[substatus]
    if (!config) return null
    const Icon = config.icon
    return (
      <span className={`badge ${config.color} flex items-center space-x-1 ml-2`}>
        <Icon className="w-3 h-3" />
        <span>{config.text}</span>
      </span>
    )
  }

  const handleBookingAction = (_action: string, booking: Booking) => {
    setSelectedBooking(booking)
    setShowModal(true)
  }

  const handleApprove = async (bookingId: string) => {
    try {
      const response = await bookingService.approveBooking(bookingId)
      if (response.success) {
        setBookings(prev => 
          prev.map(booking => 
            booking._id === bookingId 
              ? response.data
              : booking
          )
        )
        setShowModal(false)
      }
    } catch (error) {
      console.error('Error approving booking:', error)
    }
  }

  const handleReject = async (bookingId: string) => {
    try {
      const response = await bookingService.rejectBooking(bookingId)
      if (response.success) {
        setBookings(prev => 
          prev.map(booking => 
            booking._id === bookingId 
              ? response.data
              : booking
          )
        )
        setShowModal(false)
      }
    } catch (error) {
      console.error('Error rejecting booking:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bookings</h1>
          <p className="text-gray-600">Manage course bookings</p>
        </div>
        <div className="text-sm text-gray-500">
          Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalCount)} of {totalCount} bookings
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Bookings', value: bookings.length, color: 'bg-blue-500' },
          { label: 'Pending', value: bookings.filter(b => b.status === 'pending').length, color: 'bg-yellow-500' },
          { label: 'Approved', value: bookings.filter(b => b.status === 'approved').length, color: 'bg-green-500' },
          { label: 'Rejected', value: bookings.filter(b => b.status === 'rejected').length, color: 'bg-red-500' },
          { label: 'Cancelled', value: bookings.filter(b => b.status === 'cancelled').length, color: 'bg-gray-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
            <div className="">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <FileText className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            {/* Search */}
            <div className="flex-1">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search bookings..."
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            >
              <option value="ALL">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* Substatus Filter */}
            <select
              value={substatusFilter}
              onChange={(e) => setSubstatusFilter(e.target.value)}
              className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            >
              <option value="ALL">All Substatus</option>
              <option value="pending approval from school">Pending Approval</option>
              <option value="pending payment">Pending Payment</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-ivory-700">Customer</th>
                <th className="text-left py-3 px-4 font-medium text-ivory-700">School</th>
                <th className="text-left py-3 px-4 font-medium text-ivory-700">Course</th>
                <th className="text-left py-3 px-4 font-medium text-ivory-700">Selected Month</th>
                <th className="text-left py-3 px-4 font-medium text-ivory-700">Status</th>
                <th className="text-left py-3 px-4 font-medium text-ivory-700">Submitted</th>
                <th className="text-left py-3 px-4 font-medium text-ivory-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, index) => (
                <motion.tr
                  key={booking._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{booking.name}</p>
                        <p className="text-sm text-gray-500">{booking.email}</p>
                        {booking.phone && (
                          <p className="text-sm text-gray-600">{booking.phone}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <School className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-900">{booking.schoolName}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-900">{booking.courseTitle || 'N/A'}</span>
                  </td>
                  <td className="py-4 px-4">
                    {booking.selectedMonth ? (
                      <span className="text-sm text-gray-900">
                        {formatBookingSelectedPeriod(booking.selectedMonth, 'long')}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center flex-wrap gap-1">
                      {getStatusBadge(booking.status)}
                      {getSubstatusBadge(booking.substatus)}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(booking.submittedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleBookingAction('view', booking)}
                        className="p-1 text-gray-600 hover:text-primary-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {booking.substatus === 'pending approval from school' && booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(booking._id)}
                            className="p-1 text-gray-600 hover:text-green-600 transition-colors"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(booking._id)}
                            className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || loading}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || loading}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      {showModal && selectedBooking && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Booking Details
                  </h3>
                  <p className="text-gray-600">{selectedBooking.name} - {selectedBooking.schoolName}</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Customer Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-ivory-700">Name</label>
                    <p className="text-gray-900">{selectedBooking.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ivory-700">Email</label>
                    <p className="text-gray-900">{selectedBooking.email}</p>
                  </div>
                  {selectedBooking.phone && (
                    <div>
                      <label className="text-sm font-medium text-ivory-700">Phone</label>
                      <p className="text-gray-900">{selectedBooking.phone}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-ivory-700">Status</label>
                    <div className="mt-1 flex items-center flex-wrap gap-1">
                      {getStatusBadge(selectedBooking.status)}
                      {getSubstatusBadge(selectedBooking.substatus)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Booking Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-ivory-700">School</label>
                    <p className="text-gray-900">{selectedBooking.schoolName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ivory-700">Course</label>
                    <p className="text-gray-900">{selectedBooking.courseTitle || 'N/A'}</p>
                  </div>
                  {selectedBooking.selectedMonth && (
                    <div>
                      <label className="text-sm font-medium text-ivory-700">Selected Month</label>
                      <p className="text-gray-900">
                        {formatBookingSelectedPeriod(selectedBooking.selectedMonth, 'long')}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-ivory-700">Submitted</label>
                    <p className="text-gray-900">
                      {new Date(selectedBooking.submittedAt).toLocaleString()}
                    </p>
                  </div>
                  {selectedBooking.approvedAt && (
                    <div>
                      <label className="text-sm font-medium text-ivory-700">Approved</label>
                      <p className="text-gray-900">
                        {new Date(selectedBooking.approvedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedBooking.rejectedAt && (
                    <div>
                      <label className="text-sm font-medium text-ivory-700">Rejected</label>
                      <p className="text-gray-900">
                        {new Date(selectedBooking.rejectedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedBooking.paymentCompletedAt && (
                    <div>
                      <label className="text-sm font-medium text-ivory-700">Payment Completed</label>
                      <p className="text-gray-900">
                        {new Date(selectedBooking.paymentCompletedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              {selectedBooking.message && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Message</h4>
                  <p className="text-ivory-700 bg-gray-50 p-4 rounded-lg">
                    {selectedBooking.message}
                  </p>
                </div>
              )}

              {/* Payment Link */}
              {selectedBooking.paymentLink && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Payment Link</h4>
                  <a 
                    href={selectedBooking.paymentLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 text-sm break-all"
                  >
                    {selectedBooking.paymentLink}
                  </a>
                </div>
              )}

              {/* Notes */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Admin Notes</h4>
                <textarea
                  className="input w-full h-24"
                  placeholder="Add notes about this booking..."
                  defaultValue={selectedBooking.notes || ''}
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary btn-md"
              >
                Close
              </button>
              {selectedBooking.substatus === 'pending approval from school' && selectedBooking.status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      handleReject(selectedBooking._id)
                    }}
                    className="btn btn-danger btn-md"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => {
                      handleApprove(selectedBooking._id)
                    }}
                    className="btn btn-success btn-md"
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Applications


